import axios from "axios";
import { setToast } from "../store/slices/toastSlice";
import { store } from "../store";
import { setUser } from "../store/slices/userSlice";
import { getUser, getUserDataWithoutDecrypt } from "../utils/types";

// Function to refresh the token
const API = axios.create({
  baseURL: process.env.API_URL,
});


const refreshAccessToken = async () => {
  try {
    const state = getUser();
    const refreshToken = state?.refreshToken;
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post(`${process.env.API_URL}/auth/refresh`, {
      refresh_token: refreshToken,
      mode: "json",
    });

    const { access_token ,refresh_token,expires} = response?.data?.data;
    let initialData=getUserDataWithoutDecrypt();
    let newData={...initialData,token:access_token,refreshToken:refresh_token,expires:expires}
    window.sessionStorage.setItem('data',JSON.stringify(newData));
    // Update the Redux store with the new token
    store.dispatch(setUser({ token: access_token }));
    return access_token;
  } catch (error) {
    throw error;
  }
};
// Add a request interceptor
API.interceptors.request.use(
  function (config) {
    // Do something before request is sent, like adding Authorization headers
    const state: any = getUser();
    const token = state?.token;
    config.headers["Accept-Language"] = `en`;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let refreshSubscribers:any = [];

// Function to notify subscribers once the token is refreshed
const onRefreshed = (newToken:any) => {
  refreshSubscribers.forEach((callback:any) => callback(newToken));
  refreshSubscribers = [];
};


API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const dispatch = store.dispatch;
    const originalRequest = error.config;
    const url=window.location.pathname
    if (url?.includes("auth/login")) {
      dispatch(
        setToast({
          message: error.response.data?.errors?.[0]?.message,
          severity: "error",
          open: true,
        })
      )
      return Promise.reject(error); 
    }


    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
       
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();         
          isRefreshing = false;
          onRefreshed(newToken);
          return API(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          refreshSubscribers = []; // Clear subscribers
          dispatch(
            setToast({
              message: "Session expired. Please log in again.",
              severity: "error",
              open: true,
            })
          );
          sessionStorage.clear();
          window.location.href = "/auth/login";
          return Promise.reject(refreshError);
        }
      }

      return new Promise((resolve) => {
        refreshSubscribers.push((newToken:any) => {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          resolve(API(originalRequest));
        });
      });
    }

    // Handle other errors
    if (error.response) {
      const errorMessage =
        error.response.data?.errors?.[0]?.message || "Unknown error";

      switch (error.response.status) {
        case 403:
          window.sessionStorage.clear();
          window.location.href = "/auth/login";
          break;
        case 500:
        default:
          dispatch(
            setToast({
              message: `${errorMessage}`,
              severity: "error",
              open: true,
            })
          );
      }
    } else {
      dispatch(
        setToast({
          message: "Network error: Please check your internet connection",
          severity: "error",
          open: true,
        })
      );
    }

    return Promise.reject(error);
  }
);

export default API;
